import React from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldAlert } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useSEO } from "../Utils/useSEO";

export default function NotFound() {
  useSEO({
    title: "404 - Page Not Found",
    description: "The page you are looking for has been moved, removed, or is temporarily offline inside the Supply Spot command center."
  });

  const location = useLocation();

  return (
    <div className="min-h-screen bg-bg-primary text-text-primary transition-colors duration-300 overflow-x-hidden font-sans pb-0 relative selection:bg-brand-orange/30 selection:text-text-primary">
      <Navbar />

      {/* Grid Overlay */}
      <div className="absolute inset-0 bg-grid-pattern-50 pointer-events-none -z-20" />

      {/* Ambient Glow Orbs */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-brand-orange/10 rounded-full blur-[140px] -z-10 pointer-events-none" />
      <div className="absolute top-1/3 left-1/3 w-[300px] h-[300px] bg-brand-gold/5 rounded-full blur-[100px] -z-10 pointer-events-none" />

      {/* 404 MAIN CONTENT */}
      <main className="max-w-4xl mx-auto px-6 pt-44 pb-32 text-center flex-1 flex flex-col items-center justify-center gap-8 relative z-10">
        
        {/* Animated Badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-orange/30 bg-brand-orange/10 text-brand-amber text-xs font-semibold uppercase tracking-wider shadow-md"
        >
          <ShieldAlert className="w-4 h-4 text-brand-orange animate-pulse" />
          <span>Sector Offline</span>
        </motion.div>

        {/* Big 404 Title */}
        <div className="relative">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-8xl sm:text-9xl font-title font-extrabold tracking-tight bg-gradient-to-r from-brand-orange via-brand-amber to-brand-gold bg-clip-text text-transparent drop-shadow-lg"
          >
            404
          </motion.h1>
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.15 }}
            transition={{ delay: 0.3 }}
            className="absolute top-0 left-1/2 -translate-x-1/2 text-[12rem] font-bold opacity-15 pointer-events-none select-none blur-sm font-title"
          >
            404
          </motion.div>
        </div>

        {/* Error Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-col gap-4 max-w-lg"
        >
          <h2 className="text-xl sm:text-2xl font-bold font-title text-text-primary">
            Route Resolution Failure
          </h2>
          <p className="text-text-secondary text-sm md:text-base leading-relaxed">
            The target node or capability path you requested does not exist or has been relocated to another coordinate in the SupplySpot network.
          </p>
        </motion.div>

        {/* Command Terminal Box */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="w-full max-w-md bg-slate-950/80 border border-slate-900 rounded-lg p-4 font-mono text-left text-xs text-slate-400 shadow-xl"
        >
          <div className="flex items-center gap-2 border-b border-slate-900 pb-2 mb-3">
            <span className="w-2.5 h-2.5 rounded-full bg-brand-orange/60"></span>
            <span className="w-2.5 h-2.5 rounded-full bg-brand-amber/60"></span>
            <span className="w-2.5 h-2.5 rounded-full bg-brand-gold/60"></span>
            <span className="ml-2 text-[10px] text-text-muted">system_terminal.sh</span>
          </div>
          <p className="text-brand-orange mb-1">$ lookup_route --url "{location.pathname}"</p>
          <p className="text-red-400 mb-2">ERROR: HOST_RESOLUTION_FAILURE: Path not registered in core routing table.</p>
          <p className="text-brand-amber">$ ping -c 1 core_gateway</p>
          <p className="text-emerald-400 animate-pulse">PING SUCCESS: Core gateway online. Re-route possible.</p>
        </motion.div>

        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-4"
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3.5 font-semibold text-xs tracking-wider text-white bg-gradient-to-r from-brand-orange to-brand-amber rounded-lg shadow-lg shadow-brand-orange/20 hover:scale-[1.02] active:scale-[0.98] transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>RETURN TO COMMAND CORE</span>
          </Link>
        </motion.div>

      </main>

      <Footer />
    </div>
  );
}
